import Swiper from 'swiper';
import {Navigation, Thumbs} from 'swiper/modules';

export const ResidencePage = {
    isEditorPreview() {
        return !!(
            document.body && (
                document.body.classList.contains('wp-admin') ||
                document.body.classList.contains('block-editor-page') ||
                document.querySelector('.block-editor') ||
                document.querySelector('.editor-styles-wrapper') ||
                window.location.pathname.includes('/wp-admin/')
            )
        );
    },

    init() {
        const isEditor = this.isEditorPreview();

        this.initHeroSticky();
        this.initTestimonialsScrollThrough();
        this.initHeroFadeOnCover();
        this.initAnchorNav();
        this.initSidebarTabs();
        this.initRoomOptions();
        this.initAgreementToggle();
        this.initShowMoreRooms();
        this.initRoomGalleries();
        this.initVcardGallerySync();
        this.initMobileRoomThumbs();
        this.initMobileRoomModal();
        this.initAmenitySliders();

        if (!isEditor) {
            this.initAboutGallery();
            this.initAboutVideo();
            this.initFixedBar();
            this.initContactModal();
            this.initVirtualTour();
        }
    },

    initHeroSticky() {
        const hero = document.querySelector('.residence-page__hero');
        if (!hero) return;

        const update = () => {
            if (window.innerWidth <= 333 && window.innerHeight <= 710) {
                hero.style.top = '';
                return;
            }

            const heroH = hero.offsetHeight;
            const viewH = window.innerHeight;
            const headerH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-h')) || 142;

            if (heroH > viewH - headerH) {
                const stickyTop = Math.max(-(heroH - viewH + headerH), -(heroH * 0.4));
                hero.style.top = stickyTop + 'px';
            } else {
                hero.style.top = headerH + 'px';
            }
        };

        update();
        window.addEventListener('resize', update);
    },

    /**
     * Progressively fade a sticky hero as a scrolling panel covers it from
     * above. Fade is tied to scroll position, not time — the hero reaches
     * opacity 0 after the cover panel's top has scrolled `data-hero-fade-vh`
     * viewport-heights above the viewport top (default 250vh).
     *
     * Opt-in via data attributes on the hero element:
     *   - data-hero-fade-target="<css-selector>"   — selector for the cover
     *     panel; fade starts when its top crosses viewport 0.
     *   - data-hero-fade-vh="<number>"             — fade distance in vh
     *     (default 250).
     *
     * Used on:
     *   - `.residence-page__hero` (cover: `.residence-page__rooms-scroll`)
     *   - `.contact-page__hero`   (cover: `.contact-page__parallax`)
     *
     * Uses a rAF-throttled scroll listener — needed for a smooth gradient
     * that follows scroll position. IntersectionObserver alone can't give
     * a continuous value.
     */
    /**
     * On short narrow phones (≤333px wide, ≤710px tall) the sticky
     * .block-testimonials is taller than the viewport, so the bottom content
     * (heading + nav buttons) is never reachable. This drives the block's
     * `top` from 0 → -overflow as the user scrolls, revealing the full block
     * progressively without compressing any content.
     */
    initTestimonialsScrollThrough() {
        const block = document.querySelector('.residence-page .block-testimonials');
        if (!block) return;

        const fixedBarH = 80;
        let ticking = false;

        const update = () => {
            ticking = false;

            if (window.innerWidth > 333 || window.innerHeight > 710) {
                block.style.top = '';
                return;
            }

            const viewH  = window.innerHeight - fixedBarH;
            const blockH = block.offsetHeight;
            const overflow = Math.max(0, blockH - viewH);
            if (overflow === 0) { block.style.top = '0px'; return; }

            // Natural (unstuck) top of the block relative to viewport.
            // offsetTop is relative to offsetParent; walk up to get page Y.
            let naturalPageY = 0;
            let el = block;
            while (el) { naturalPageY += el.offsetTop; el = el.offsetParent; }
            const naturalViewportTop = naturalPageY - window.scrollY;

            if (naturalViewportTop >= 0) {
                // Not yet sticky — reset.
                block.style.top = '0px';
                return;
            }

            // Interpolate top from 0 to -overflow as we scroll deeper.
            const past = -naturalViewportTop;
            block.style.top = -Math.min(past, overflow) + 'px';
        };

        window.addEventListener('scroll', () => {
            if (!ticking) { requestAnimationFrame(update); ticking = true; }
        }, { passive: true });

        window.addEventListener('resize', update);
        update();
    },

    initHeroFadeOnCover() {
        document.querySelectorAll('[data-hero-fade-target]').forEach((hero) => {
            const target = hero.dataset.heroFadeTarget;
            // Special-case "next" to pick up the next sibling — handy when
            // the cover is the next block in document order but its CSS
            // selector varies per page (e.g. on Help & Advice the first
            // post-hero block can be Academic Support OR Support Struct
            // depending on the content the editor placed).
            const cover = (target === 'next')
                ? hero.nextElementSibling
                : document.querySelector(target);
            if (!cover) return;

            const fadeVh = parseFloat(hero.dataset.heroFadeVh) || 250;

            let startY = 0;     // page Y at which cover.top reaches viewport top
            let distance = 0;   // total fade distance in pixels
            let ticking = false;

            // Smooth ease-in cubic — fade barely happens for the first
            // 30% of scroll, then accelerates. Feels less abrupt than
            // linear, hero stays present longer.
            const ease = (t) => t * t * t;

            const measure = () => {
                const rect = cover.getBoundingClientRect();
                startY = window.scrollY + rect.top;
                distance = (fadeVh / 100) * window.innerHeight;
            };

            const update = () => {
                ticking = false;
                const raw = (window.scrollY - startY) / distance;
                const progress = Math.max(0, Math.min(1, raw));
                const opacity = 1 - ease(progress);
                hero.style.opacity = String(opacity);
                hero.style.visibility = opacity <= 0.001 ? 'hidden' : '';
            };

            const onScroll = () => {
                if (!ticking) {
                    requestAnimationFrame(update);
                    ticking = true;
                }
            };

            const onResize = () => {
                measure();
                update();
            };

            measure();
            update();
            window.addEventListener('scroll', onScroll, {passive: true});
            window.addEventListener('resize', onResize);
        });
    },

    initAnchorNav() {
        const nav = document.querySelector('.residence-page__anchor-nav');
        if (!nav) return;
        const pageRoot = document.querySelector('.residence-page');
        const inner = nav.querySelector('.residence-page__anchor-inner');
        const arrow = nav.querySelector('.residence-page__anchor-arrow');

        const links = Array.from(nav.querySelectorAll('.residence-page__anchor-link[href^="#"]'));
        if (!links.length) return;

        const items = links.map((link) => {
            const id = link.getAttribute('href')?.slice(1);
            if (!id) return null;
            const section = document.getElementById(id);
            if (!section) return null;
            return {link, section, id};
        }).filter(Boolean);

        if (!items.length) return;

        // ── Arrow overflow logic ──
        const updateArrow = () => {
            if (!arrow || !inner) return;
            const isOverflowing = inner.scrollWidth > inner.clientWidth;
            const isAtEnd = inner.scrollLeft + inner.clientWidth >= inner.scrollWidth - 4;

            if (isOverflowing) {
                arrow.classList.add('is-visible');
                arrow.classList.toggle('is-end', isAtEnd);
            } else {
                arrow.classList.remove('is-visible');
            }
        };

        if (arrow && inner) {
            arrow.addEventListener('click', () => {
                inner.scrollBy({left: 200, behavior: 'smooth'});
            });
            inner.addEventListener('scroll', updateArrow, {passive: true});
            window.addEventListener('resize', updateArrow);
            updateArrow();
        }

        let activeId = '';
        let isClickScrolling = false;
        let ticking = false;
        const clickOffset = 220;

        const setActive = (nextId) => {
            if (!nextId || nextId === activeId) return;
            activeId = nextId;
            items.forEach(({link, id}) => {
                const isActive = id === nextId;
                link.classList.toggle('is-active', isActive);
                link.toggleAttribute('aria-current', isActive);
                if (isActive) {
                    link.scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'center'});
                }
            });
            // Update arrow after active link scrolls into view
            setTimeout(updateArrow, 350);
        };

        const updateActiveSection = () => {
            ticking = false;
            if (isClickScrolling) return;

            let current = items[0].id;

            items.forEach(({section, id}, index) => {
                if (index === 0) return;
                if (section.getBoundingClientRect().top <= clickOffset) {
                    current = id;
                }
            });

            setActive(current);
        };

        const requestUpdate = () => {
            if (ticking) return;
            ticking = true;
            window.requestAnimationFrame(updateActiveSection);
        };

        window.addEventListener('scroll', requestUpdate, {passive: true});
        window.addEventListener('resize', requestUpdate);

        // Click handling: smooth scroll to section
        links.forEach((link) => {
            link.addEventListener('click', (e) => {
                const nextId = link.getAttribute('href')?.slice(1);
                if (!nextId) return;
                e.preventDefault();

                const target = document.getElementById(nextId);
                if (!target) return;

                setActive(nextId);
                isClickScrolling = true;

                const targetY = nextId === 'overview' && pageRoot
                    ? pageRoot.getBoundingClientRect().top + window.scrollY
                    : target.getBoundingClientRect().top + window.scrollY - clickOffset;

                window.scrollTo({top: Math.max(targetY, 0), behavior: 'smooth'});

                let scrollTimer;
                const onScrollEnd = () => {
                    clearTimeout(scrollTimer);
                    scrollTimer = setTimeout(() => {
                        isClickScrolling = false;
                        window.removeEventListener('scroll', onScrollEnd);
                        updateActiveSection();
                    }, 100);
                };
                window.addEventListener('scroll', onScrollEnd, {passive: true});

                if (window.location.hash) {
                    history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
                }
            });
        });

        // Activate first item on load
        updateActiveSection();

        // Deep-link from a listing card: /residence/#<room-section-id> (built
        // by "View room" in template-parts/room-card.php) must LAND on that
        // room section, not leave it hidden under the sticky anchor-nav. The
        // browser's native hash jump mis-fires here: room sections use
        // `content-visibility: auto` (an 800px intrinsic estimate) so the
        // target's real offset isn't known until rendered, and a native jump
        // has no sticky-header offset. Do it ourselves.
        const hashId = window.location.hash ? decodeURIComponent(window.location.hash.slice(1)) : '';
        if (hashId) {
            const hashItem = items.find(({id}) => id === hashId);
            const targetEl = hashItem ? hashItem.section : document.getElementById(hashId);
            if (targetEl) {
                // Un-defer every room section so the heights ABOVE the target
                // are real before we measure (content-visibility:auto otherwise
                // reports the intrinsic estimate and the scroll lands short).
                document.querySelectorAll('.room-section').forEach((s) => {
                    s.style.contentVisibility = 'visible';
                });
                // The page sets `html { scroll-behavior: smooth }`. On a hash
                // load that turns the native jump into an animation which races
                // our jump AND the content-visibility reflow above — they cancel
                // and the page is left at the top. Force instant scrolling for
                // the deep-link, then restore so nav clicks stay smooth.
                const rootStyle = document.documentElement.style;
                const prevBehavior = rootStyle.scrollBehavior;
                rootStyle.scrollBehavior = 'auto';
                const landOnTarget = () => {
                    const y = targetEl.getBoundingClientRect().top + window.scrollY - clickOffset;
                    window.scrollTo(0, Math.max(y, 0));
                };
                // Two frames let the un-deferred layout + the ?room= variant
                // switch (initRoomOptions) settle; a follow-up pass corrects any
                // late image reflow before restoring smooth scrolling.
                requestAnimationFrame(() => requestAnimationFrame(() => {
                    landOnTarget();
                    if (hashItem) setActive(hashId);
                    setTimeout(() => {
                        landOnTarget();
                        rootStyle.scrollBehavior = prevBehavior;
                    }, 250);
                }));
            }
        }
    },

    initSidebarTabs() {
        document.querySelectorAll('[data-sidebar-tabs]').forEach((container) => {
            if (container.dataset.thriveInit === '1') return;
            container.dataset.thriveInit = '1';
            const tabs = container.querySelectorAll('[data-sidebar-tab]');
            const panels = container.closest('.residence-page__sidebar-form')?.querySelectorAll('[data-sidebar-panel]');
            if (!tabs.length || !panels) return;

            tabs.forEach((tab) => {
                tab.addEventListener('click', () => {
                    const target = tab.getAttribute('data-sidebar-tab');
                    tabs.forEach((t) => t.classList.remove('is-active'));
                    tab.classList.add('is-active');
                    panels.forEach((p) => {
                        p.classList.toggle('is-active', p.getAttribute('data-sidebar-panel') === target);
                    });
                    // Nudge embedded HubSpot iframes to recompute height.
                    window.dispatchEvent(new Event('resize'));
                });
            });
        });
    },

    initRoomOptions() {
        document.querySelectorAll('[data-room-options]').forEach((optionsContainer) => {
            if (optionsContainer.dataset.thriveInit === '1') return;
            optionsContainer.dataset.thriveInit = '1';
            const section = optionsContainer.closest('.room-section');
            if (!section) return;

            const buttons = optionsContainer.querySelectorAll('[data-room-option]');
            const variantPanels = section.querySelectorAll('[data-variant-panel]');

            const selectOption = (idx) => {
                // Toggle active button.
                buttons.forEach((b) => b.classList.toggle('is-active', b.dataset.roomOption === idx));

                // Switch variant content panel.
                variantPanels.forEach((panel) => {
                    const isActive = panel.dataset.variantPanel === idx;
                    panel.classList.toggle('is-active', isActive);

                    // Init swiper on newly active panel if not already done.
                    if (isActive) {
                        const swiperEl = panel.querySelector('[data-room-swiper]');
                        if (swiperEl && !swiperEl.swiper) {
                            ResidencePage.initSingleSwiper(swiperEl);
                        }
                    }
                });

                // Switch the config panel (residence DS 3-level layout) and init
                // its gallery swiper on first reveal.
                section.querySelectorAll('[data-config-panel]').forEach((panel) => {
                    const isActive = panel.dataset.configPanel === idx;
                    panel.classList.toggle('is-active', isActive);
                    if (isActive) {
                        const swiperEl = panel.querySelector('[data-room-swiper]');
                        if (swiperEl && !swiperEl.swiper) {
                            ResidencePage.initSingleSwiper(swiperEl);
                        }
                    }
                });

                // Sync the room-variation cards (legacy card layout): highlight
                // the card matching the active variation, when present.
                section.querySelectorAll('[data-room-card]').forEach((card) => {
                    card.classList.toggle('is-active', card.dataset.roomCard === idx);
                });

                // Update dropdown UI if present.
                const selectWrapper = section.querySelector('.room-section__options-select');
                const dropdownItems = section.querySelectorAll('[data-dropdown-option]');
                if (selectWrapper) {
                    const activeItem = section.querySelector(`[data-dropdown-option="${idx}"]`);
                    if (activeItem) {
                        const nameEl = selectWrapper.querySelector('.room-section__options-select-name');
                        const priceEl = selectWrapper.querySelector('.room-section__options-select-price');
                        if (nameEl) nameEl.textContent = activeItem.dataset.optionName || '';
                        if (priceEl) priceEl.innerHTML = activeItem.dataset.optionPrice || '';
                    }
                    dropdownItems.forEach((item) => item.classList.toggle('is-active', item.dataset.dropdownOption === idx));
                }
            };

            buttons.forEach((btn) => {
                btn.addEventListener('click', () => {
                    selectOption(btn.dataset.roomOption);
                });
            });

            // Cards double as selectors — clicking a card shows its detail on
            // the left. Clicks on the inner Apply link/button pass through.
            section.querySelectorAll('[data-room-card]').forEach((card) => {
                card.addEventListener('click', (e) => {
                    if (e.target.closest('a, button')) return;
                    // Only the legacy variant-panel layout uses cards as config
                    // selectors (data-room-card="<idx>"). In the DS config layout a
                    // config has MANY cards with an EMPTY data-room-card, so a click
                    // must not switch config — selectOption('') matches no panel and
                    // would hide the whole section.
                    const cardIdx = card.dataset.roomCard;
                    if (cardIdx === '' || cardIdx == null) return;
                    selectOption(cardIdx);
                });
            });

            // Deep-link from the listing card: ?room=<variant-slug> opens that
            // exact variant so the page matches the card the user clicked
            // (e.g. "Single Room in 3-Bed Apartment", not just the first one).
            const deepLinkSlug = new URLSearchParams(window.location.search).get('room');
            if (deepLinkSlug) {
                const match = Array.from(buttons).find((b) => b.dataset.variantSlug === deepLinkSlug);
                if (match) selectOption(match.dataset.roomOption);
            }

            // Mobile dropdown toggle
            const selectWrapper = section.querySelector('.room-section__options-select');
            if (selectWrapper) {
                const selectBtn = selectWrapper.querySelector('.room-section__options-select-btn');
                const dropdownItems = section.querySelectorAll('[data-dropdown-option]');

                if (selectBtn) {
                    selectBtn.addEventListener('click', (e) => {
                        e.stopPropagation();
                        selectWrapper.classList.toggle('is-open');
                    });
                }

                dropdownItems.forEach((item) => {
                    item.addEventListener('click', () => {
                        const idx = item.dataset.dropdownOption;
                        selectWrapper.classList.remove('is-open');
                        selectOption(idx);
                    });
                });

                // Close dropdown when clicking outside
                document.addEventListener('click', (e) => {
                    if (!selectWrapper.contains(e.target)) {
                        selectWrapper.classList.remove('is-open');
                    }
                });
            }
        });
    },

    // Agreement-length toggle (12 / 11 / 10 months). Picking a length shows the
    // matching price tier and hides the others across the whole pricing group.
    // Default is whatever the server rendered active (12 months per Figma).
    // Display only — the selected length is not sent to FG Nexus yet.
    initAgreementToggle() {
        document.querySelectorAll('[data-agreement-group]').forEach((group) => {
            if (group.dataset.thriveAgreement === '1') return;
            const toggles = group.querySelectorAll('[data-agreement-toggle]');
            if (toggles.length < 2) return; // Nothing to switch.
            group.dataset.thriveAgreement = '1';

            // Cards (DS layout): each holds its own price tiers + Apply link.
            // Fallback to the legacy single-panel group when there are no cards.
            const cards = group.querySelectorAll('[data-room-card]');
            const applyLinks = group.querySelectorAll('a[data-apply-cta]');

            const setActiveTier = (scope, months) => {
                const tiers = scope.querySelectorAll('[data-agreement-months]');
                if (!tiers.length) return;
                let matched = false;
                let activeTier = null;
                tiers.forEach((t) => {
                    const on = t.dataset.agreementMonths === months;
                    t.classList.toggle('is-active', on);
                    if (on) {
                        matched = true;
                        activeTier = t;
                    }
                });

                // A card represents one exact FG Nexus room type. Never fall
                // back to its first tier: doing so made a 12-month price look
                // like a selected 10-month agreement. ACF tiers absent from
                // Nexus remain visible as Fully let and link to the wait list.
                if (scope.matches('[data-lease-card]')) {
                    scope.hidden = !matched;
                    scope.classList.toggle('is-unavailable-for-term', !matched);

                    const cta = scope.querySelector('[data-apply-cta]');
                    if (cta && matched) {
                        const leaseStatus = activeTier ? activeTier.dataset.leaseStatus : '';
                        const isWaitlist = leaseStatus === 'fully_let' || leaseStatus === 'not_offered';
                        const text = cta.querySelector('.btn__text');
                        const url = isWaitlist ? cta.dataset.waitlistUrl : cta.dataset.bookingUrl;
                        if (url) cta.href = url;
                        cta.dataset.leaseMode = isWaitlist ? 'waitlist' : 'apply';
                        cta.classList.toggle('btn--outlined', isWaitlist);
                        cta.classList.toggle('btn--filled', !isWaitlist);
                        if (text) text.textContent = isWaitlist ? 'Join the waiting list' : 'Apply now';

                        // Manual/hidden badge overrides remain editor-owned.
                        // In Auto mode a full or not-currently-offered term uses
                        // the existing red Fully let state from the design.
                        if (scope.dataset.autoLeaseBadge === '1') {
                            const badge = scope.querySelector('.room-vcard__badge');
                            if (badge) {
                                const badgeKey = isWaitlist ? 'fully_let' : scope.dataset.baseBadgeKey;
                                const badgeLabel = isWaitlist ? 'Fully let' : scope.dataset.baseBadgeLabel;
                                badge.classList.remove(
                                    'residence-badge--available',
                                    'residence-badge--almost_full',
                                    'residence-badge--fully_let'
                                );
                                if (badgeKey) badge.classList.add(`residence-badge--${badgeKey}`);
                                badge.textContent = badgeLabel || '';
                                badge.hidden = !badgeLabel;
                            }
                        }
                    }
                }
            };

            const apply = (months) => {
                toggles.forEach((t) => {
                    const on = t.dataset.agreementToggle === months;
                    t.classList.toggle('is-active', on);
                    t.setAttribute('aria-selected', on ? 'true' : 'false');
                });
                if (cards.length) {
                    cards.forEach((c) => setActiveTier(c, months));
                } else {
                    setActiveTier(group, months);
                }
                // Keep bookable cards carrying the chosen term to FG Nexus.
                // A full term instead points at its wait-list URL; appending
                // `months` to that non-handover URL would be meaningless.
                applyLinks.forEach((link) => {
                    try {
                        if (link.dataset.leaseMode === 'waitlist') return;
                        const u = new URL(link.href, window.location.origin);
                        u.searchParams.set('months', months);
                        link.href = u.toString();
                    } catch (e) { /* leave href untouched */ }
                });
            };

            toggles.forEach((t) => {
                t.addEventListener('click', () => apply(t.dataset.agreementToggle));
            });

            // Sync to the server-rendered default (12 months).
            const activeBtn = group.querySelector('.room-section__agreement.is-active') || toggles[0];
            if (activeBtn) apply(activeBtn.dataset.agreementToggle);
        });
    },

    // Reveal room-variation cards hidden behind the "Show more rooms" button
    // (Figma: max 4 cards up front, rest lazy-revealed).
    initShowMoreRooms() {
        document.querySelectorAll('[data-room-showmore]').forEach((btn) => {
            if (btn.dataset.thriveInit === '1') return;
            btn.dataset.thriveInit = '1';
            btn.addEventListener('click', () => {
                const right = btn.closest('.room-section__right');
                if (right) {
                    right.querySelectorAll('.room-vcard--overflow').forEach((c) => c.classList.remove('room-vcard--overflow'));
                }
                btn.remove();
            });
        });
    },

    // Init a single room swiper element.
    initSingleSwiper(el) {
        const container = el.closest('.room-section__gallery');
        if (!container) return;
        if (el.swiper) return; // Idempotent for editor re-mounts.

        new Swiper(el, {
            modules: [Navigation],
            slidesPerView: 1,
            loop: true,
            simulateTouch: true,
            navigation: {
                prevEl: container.querySelector('[data-room-prev]'),
                nextEl: container.querySelector('[data-room-next]'),
            },
        });
    },

    initRoomGalleries() {
        // Only init swipers in active variant panels (or panels without variant system).
        document.querySelectorAll('[data-room-swiper]').forEach((el) => {
            const panel = el.closest('[data-variant-panel], [data-config-panel]');
            if (panel && !panel.classList.contains('is-active')) return; // Skip hidden panels.
            this.initSingleSwiper(el);
        });
    },

    initAmenitySliders() {
        document.querySelectorAll('[data-amenity-swiper]').forEach((wrapper) => {
            const swiperEl = wrapper.querySelector('.swiper');
            const group = wrapper.closest('.amenities-section__group');
            if (!swiperEl || !group) return;
            if (swiperEl.swiper) return; // Idempotent for editor re-mounts.

            const prevBtn = group.querySelector('[data-amenity-prev]');
            const nextBtn = group.querySelector('[data-amenity-next]');
            const counterEl = group.querySelector('[data-amenity-counter]');
            const paginationEl = group.querySelector('[data-amenity-pagination]');
            const itemCount = parseInt(group.dataset.itemCount, 10) || 0;

            const swiper = new Swiper(swiperEl, {
                modules: [Navigation],
                // Per Figma 567:48105 / 567:139671 cards are a fixed ~320px
                // wide regardless of context. Auto-fit lets us show 3 in the
                // narrower residence column (with sidebar) and 5 in the wider
                // short-stay column without two sets of breakpoints.
                slidesPerView: 'auto',
                spaceBetween: 32,
                simulateTouch: true,
                navigation: {
                    prevEl: prevBtn,
                    nextEl: nextBtn,
                },
                breakpoints: {
                    0:    {slidesPerView: 1.15, spaceBetween: 16},
                    481:  {slidesPerView: 1.5,  spaceBetween: 16},
                    700:  {slidesPerView: 2.05, spaceBetween: 24},
                    // From the desktop breakpoint up, switch to 'auto' so the
                    // CSS card width (.amenities-section__cards .swiper-slide)
                    // governs how many fit per row.
                    1025: {slidesPerView: 'auto', spaceBetween: 32},
                },
            });

            // Show/hide pagination based on whether Swiper can actually scroll
            const updatePaginationVisibility = () => {
                if (!paginationEl) return;
                const canScroll = swiper.snapGrid.length > 1;
                paginationEl.style.display = canScroll ? '' : 'none';
            };

            if (counterEl) {
                const current = counterEl.querySelector('.amenities-section__counter-current');
                const total = counterEl.querySelector('.amenities-section__counter-total');
                const updateCounter = () => {
                    if (current) {
                        const page = swiper.snapIndex + 1;
                        current.textContent = String(page).padStart(2, '0');
                    }
                    if (total) {
                        total.textContent = String(Math.max(swiper.snapGrid.length, 1)).padStart(2, '0');
                    }
                    updatePaginationVisibility();
                };
                updateCounter();
                swiper.on('slideChange', updateCounter);
                swiper.on('breakpoint', updateCounter);
                swiper.on('resize', updateCounter);
            } else {
                updatePaginationVisibility();
                swiper.on('breakpoint', updatePaginationVisibility);
                swiper.on('resize', updatePaginationVisibility);
            }
        });
    },

    /**
     * About-section gallery lightbox. The "View the gallery" button opens a
     * full-screen modal with a Swiper (main image + thumbnail strip), caption,
     * "X of Y" counter, prev/next buttons and keyboard nav (Esc / arrows).
     * Swipers are built on first open so hidden-container sizing is correct.
     */
    initAboutGallery() {
        const modal = document.querySelector('[data-gallery-modal]');
        const openBtns = document.querySelectorAll('[data-gallery-open]');
        if (!modal || !openBtns.length || modal.dataset.thriveInit === '1') return;
        modal.dataset.thriveInit = '1';

        // Move to <body> so position:fixed measures against the viewport.
        if (modal.parentElement !== document.body) {
            document.body.appendChild(modal);
        }

        const mainEl = modal.querySelector('[data-gallery-main]');
        const thumbsEl = modal.querySelector('[data-gallery-thumbs]');
        const captionEl = modal.querySelector('[data-gallery-caption]');
        const counterEl = modal.querySelector('[data-gallery-counter]');
        const slides = modal.querySelectorAll('[data-gallery-main] .swiper-slide');
        const total = slides.length;
        if (!mainEl || !total) return;

        let mainSwiper = null;

        const syncInfo = () => {
            if (!mainSwiper) return;
            const i = mainSwiper.activeIndex || 0;
            if (captionEl) captionEl.textContent = slides[i] ? (slides[i].dataset.caption || '') : '';
            if (counterEl) counterEl.textContent = `${i + 1} of ${total}`;
        };

        const buildSwiper = () => {
            if (mainSwiper) return;
            let thumbsSwiper = null;
            if (thumbsEl) {
                thumbsSwiper = new Swiper(thumbsEl, {
                    slidesPerView: 'auto',
                    spaceBetween: 16,
                    watchSlidesProgress: true,
                    centerInsufficientSlides: true,
                });
            }
            mainSwiper = new Swiper(mainEl, {
                modules: thumbsSwiper ? [Navigation, Thumbs] : [Navigation],
                slidesPerView: 1,
                spaceBetween: 24,
                simulateTouch: true,
                navigation: {
                    prevEl: modal.querySelector('[data-gallery-prev]'),
                    nextEl: modal.querySelector('[data-gallery-next]'),
                },
                ...(thumbsSwiper ? { thumbs: { swiper: thumbsSwiper } } : {}),
            });
            mainSwiper.on('slideChange', syncInfo);
            syncInfo();
        };

        const open = (index = 0) => {
            modal.classList.add('is-open');
            modal.setAttribute('aria-hidden', 'false');
            document.body.style.overflow = 'hidden';
            buildSwiper(); // container is now visible → correct Swiper sizing
            if (mainSwiper) {
                mainSwiper.update();
                mainSwiper.slideTo(index, 0);
                syncInfo();
            }
        };

        const close = () => {
            modal.classList.remove('is-open');
            modal.setAttribute('aria-hidden', 'true');
            document.body.style.overflow = '';
        };

        openBtns.forEach((btn) => btn.addEventListener('click', () => open(0)));
        modal.querySelectorAll('[data-gallery-close]').forEach((btn) => btn.addEventListener('click', close));
        document.addEventListener('keydown', (e) => {
            if (!modal.classList.contains('is-open')) return;
            if (e.key === 'Escape') close();
            else if (e.key === 'ArrowLeft' && mainSwiper) mainSwiper.slidePrev();
            else if (e.key === 'ArrowRight' && mainSwiper) mainSwiper.slideNext();
        });
    },

    /**
     * About-section video: custom play button over the poster. Clicking it plays
     * the video, enables native controls and fades the overlay out (the
     * `is-playing` class drives the CSS transition). Native controls are added
     * only on play so the poster stays clean until then.
     */
    initAboutVideo() {
        document.querySelectorAll('[data-video]').forEach((wrap) => {
            if (wrap.dataset.thriveInit === '1') return;
            wrap.dataset.thriveInit = '1';

            const video = wrap.querySelector('video');
            const btn = wrap.querySelector('[data-video-play]');
            if (!video || !btn) return;

            const start = () => {
                video.controls = true;
                const p = video.play();
                if (p && p.catch) p.catch(() => {});
            };

            btn.addEventListener('click', start);
            // Overlay follows the play/pause state: hidden while playing, shown
            // again when the user pauses or the video ends.
            video.addEventListener('play', () => wrap.classList.add('is-playing'));
            video.addEventListener('pause', () => wrap.classList.remove('is-playing'));
            video.addEventListener('ended', () => wrap.classList.remove('is-playing'));
        });
    },

    initFixedBar() {
        const bar = document.querySelector('[data-fixed-bar]');
        const sidebar = document.querySelector('.residence-page__sidebar-sticky');
        if (!bar || !sidebar) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                const hidden = entry.isIntersecting;
                bar.classList.toggle('is-visible', !hidden);
                bar.setAttribute('aria-hidden', hidden ? 'true' : 'false');
            },
            {threshold: 0}
        );
        observer.observe(sidebar);

        bar.querySelectorAll('[data-fixed-bar-action]').forEach((btn) => {
            btn.addEventListener('click', () => {
                const action = btn.dataset.fixedBarAction;
                const modal = document.querySelector('[data-contact-modal]');
                if (modal) {
                    modal.classList.add('is-open');
                    modal.setAttribute('aria-hidden', 'false');
                    document.body.style.overflow = 'hidden';

                    const tabs = modal.querySelectorAll('[data-modal-tab]');
                    const panels = modal.querySelectorAll('[data-modal-panel]');
                    tabs.forEach((t) => t.classList.toggle('is-active', t.dataset.modalTab === action));
                    panels.forEach((p) => p.classList.toggle('is-active', p.dataset.modalPanel === action));
                    window.dispatchEvent(new Event('resize'));
                }
            });
        });
    },

    initContactModal() {
        const modal = document.querySelector('[data-contact-modal]');
        if (!modal) return;

        const closeModal = () => {
            modal.classList.remove('is-open');
            modal.setAttribute('aria-hidden', 'true');
            document.body.style.overflow = '';
        };

        modal.querySelectorAll('[data-contact-modal-close]').forEach((btn) => {
            btn.addEventListener('click', closeModal);
        });

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && modal.classList.contains('is-open')) {
                closeModal();
            }
        });

        const tabs = modal.querySelectorAll('[data-modal-tab]');
        const panels = modal.querySelectorAll('[data-modal-panel]');

        tabs.forEach((tab) => {
            tab.addEventListener('click', () => {
                const target = tab.dataset.modalTab;
                tabs.forEach((t) => t.classList.toggle('is-active', t.dataset.modalTab === target));
                panels.forEach((p) => p.classList.toggle('is-active', p.dataset.modalPanel === target));
                window.dispatchEvent(new Event('resize'));
            });
        });
    },

    /**
     * Virtual tour (3D model) modal. Opens a full-screen overlay (below the site
     * header) containing a StreamPixel iframe.
     *
     * StreamPixel is live pixel-streaming — the iframe starts a paid GPU session
     * the moment it has a `src`, so the src is never in the page markup (it lives
     * in `data-tour-src`). The iframe is built on OPEN and destroyed on CLOSE,
     * which releases the StreamPixel session. Exactly one session runs, only
     * while the tour is open — no page-load hit and no leaked sessions (which
     * exhaust StreamPixel's concurrency limit and make the stream come up blank).
     */
    initVirtualTour() {
        const modal = document.querySelector('[data-tour-modal]');
        if (!modal || modal.dataset.thriveInit === '1') return;
        modal.dataset.thriveInit = '1';

        // The modal is emitted deep inside the block markup. Relocate it to
        // <body> so its `position: fixed` is measured against the viewport and
        // can't be trapped by a transformed/opacity ancestor on the page.
        if (modal.parentElement !== document.body) {
            document.body.appendChild(modal);
        }

        const embed = modal.querySelector('[data-tour-embed]');
        const openBtns = document.querySelectorAll('[data-tour-open]');
        if (!embed || !openBtns.length) return;

        const buildIframe = () => {
            if (embed.querySelector('iframe')) return;
            const src = embed.dataset.tourSrc;
            if (!src) return;
            const iframe = document.createElement('iframe');
            iframe.src = src;
            iframe.title = 'Virtual tour';
            iframe.setAttribute('allow', 'autoplay; microphone; clipboard-read; clipboard-write; fullscreen; xr-spatial-tracking');
            iframe.setAttribute('allowfullscreen', '');
            embed.appendChild(iframe);
        };

        const teardownIframe = () => {
            const iframe = embed.querySelector('iframe');
            if (iframe) iframe.remove(); // Releases the StreamPixel session.
        };

        // The fixed site header (logo bar + residence anchor-nav) can be taller
        // than --header-h, so measure its real bottom edge and start the modal
        // just below it — keeps the whole header visible instead of clipping the
        // anchor-nav row.
        const positionBelowHeader = () => {
            const bottoms = ['.site-header', '.residence-page__anchor-nav']
                .map((sel) => document.querySelector(sel))
                .filter(Boolean)
                .map((el) => el.getBoundingClientRect().bottom);
            modal.style.top = (bottoms.length ? Math.max(0, Math.ceil(Math.max(...bottoms))) : 0) + 'px';
        };

        const open = () => {
            buildIframe(); // starts the StreamPixel session
            positionBelowHeader();
            modal.classList.add('is-open');
            modal.setAttribute('aria-hidden', 'false');
            document.body.style.overflow = 'hidden';
        };

        const close = () => {
            modal.classList.remove('is-open');
            modal.setAttribute('aria-hidden', 'true');
            document.body.style.overflow = '';
            teardownIframe(); // release the StreamPixel session
        };

        openBtns.forEach((btn) => btn.addEventListener('click', open));
        modal.querySelectorAll('[data-tour-close]').forEach((btn) => btn.addEventListener('click', close));
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && modal.classList.contains('is-open')) close();
        });
        window.addEventListener('resize', () => {
            if (modal.classList.contains('is-open')) positionBelowHeader();
        });
    },

    // Sync the config gallery to whichever room-vcard is active.
    // On click: mark that card is-active, swap gallery slides from its
    // data-images attribute, and reinitialise the Swiper.
    initVcardGallerySync() {
        document.querySelectorAll('.room-section__config').forEach((config) => {
            if (config.dataset.thriveVcardGallery === '1') return;
            config.dataset.thriveVcardGallery = '1';

            const galleryEl = config.querySelector('[data-vcard-gallery]');
            if (!galleryEl) return;

            const swiperEl = galleryEl.querySelector('[data-room-swiper]');
            const wrapper  = swiperEl ? swiperEl.querySelector('.swiper-wrapper') : null;
            const navEl    = galleryEl.querySelector('.room-section__gallery-nav');
            if (!swiperEl || !wrapper) return;

            const cards = Array.from(config.querySelectorAll('[data-room-card][data-images]'));
            if (!cards.length) return;

            const activate = (card) => {
                cards.forEach((c) => c.classList.toggle('is-active', c === card));

                let images = [];
                try { images = JSON.parse(card.dataset.images || '[]'); } catch {}

                if (!images.length) return; // No room-specific gallery — keep current.

                // Destroy, rebuild slides, reinitialise.
                if (swiperEl.swiper) {
                    swiperEl.swiper.destroy(true, true);
                }

                wrapper.innerHTML = images.map((img) =>
                    `<div class="swiper-slide"><img src="${img.url}" alt="${img.alt}" class="room-section__gallery-img" loading="lazy" draggable="false"></div>`
                ).join('');

                this.initSingleSwiper(swiperEl);

                if (navEl) {
                    navEl.style.display = images.length > 1 ? '' : 'none';
                }
            };

            cards.forEach((card) => {
                card.addEventListener('click', (e) => {
                    if (window.innerWidth <= 1024) return; // Mobile/tablet uses the bottom-sheet modal instead.
                    if (e.target.closest('a, button')) return;
                    activate(card);
                });
            });
        });
    },

    // Populate the thumbnail <img> in each mobile room card from data-images[0].
    // Falls back to the first slide in the config panel's gallery swiper.
    initMobileRoomThumbs() {
        document.querySelectorAll('.room-vcard[data-images]').forEach((card) => {
            const thumbImg = card.querySelector('.room-vcard__thumb-img');
            if (!thumbImg) return;

            let images = [];
            try { images = JSON.parse(card.dataset.images || '[]'); } catch {}

            if (images.length) {
                thumbImg.src    = images[0].url;
                thumbImg.alt    = images[0].alt || '';
            } else {
                // Fall back to the config panel's first gallery slide.
                const config     = card.closest('.room-section__config');
                const firstSlide = config?.querySelector('.swiper-slide img');
                if (firstSlide) {
                    thumbImg.src = firstSlide.src;
                    thumbImg.alt = firstSlide.alt || '';
                }
            }
        });
    },

    // Mobile/tablet bottom-sheet modal — opens when a room card is tapped on
    // screens ≤ 1024px. Shows the room gallery (with thumbnail strip), feature
    // chips, and the Apply CTA. Figma: 3180-535.
    initMobileRoomModal() {
        if (document.querySelector('.room-modal-overlay')) return; // idempotent

        const overlay = document.createElement('div');
        overlay.className = 'room-modal-overlay';
        overlay.innerHTML = `
            <div class="room-modal" role="dialog" aria-modal="true" aria-label="Room details">
                <div class="room-modal__header">
                    <p class="room-modal__title"></p>
                    <button type="button" class="room-modal__close" aria-label="Close">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                    </button>
                </div>
                <div class="room-modal__badge-wrap"></div>
                <p class="room-modal__features-label">Features</p>
                <div class="room-modal__chips"></div>
                <div class="room-modal__gallery">
                    <div class="swiper room-modal__swiper">
                        <div class="swiper-wrapper"></div>
                    </div>
                    <div class="room-modal__gallery-nav">
                        <button type="button" class="room-modal__gallery-prev" aria-label="Previous">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                <path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                            </svg>
                        </button>
                        <button type="button" class="room-modal__gallery-next" aria-label="Next">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                <path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                            </svg>
                        </button>
                    </div>
                </div>
                <div class="room-modal__thumbs"></div>
                <div class="room-modal__footer">
                    <div class="room-modal__price-row"></div>
                    <div class="room-modal__apply-wrap"></div>
                </div>
            </div>
        `;
        document.body.appendChild(overlay);

        const sheet      = overlay.querySelector('.room-modal');
        const titleEl    = sheet.querySelector('.room-modal__title');
        const badgeWrap  = sheet.querySelector('.room-modal__badge-wrap');
        const chipsEl    = sheet.querySelector('.room-modal__chips');
        const swiperWrap = sheet.querySelector('.swiper-wrapper');
        const thumbsEl   = sheet.querySelector('.room-modal__thumbs');
        const priceRow   = sheet.querySelector('.room-modal__price-row');
        const applyWrap  = sheet.querySelector('.room-modal__apply-wrap');
        const prevBtn    = sheet.querySelector('.room-modal__gallery-prev');
        const nextBtn    = sheet.querySelector('.room-modal__gallery-next');
        const navEl      = sheet.querySelector('.room-modal__gallery-nav');

        let modalSwiper = null;

        const openModal = (card) => {
            // Title
            const descEl = card.querySelector('.room-vcard__desc');
            titleEl.textContent = descEl ? descEl.textContent : '';

            // Availability badge
            const badgeEl = card.querySelector('.room-vcard__badge');
            badgeWrap.innerHTML = badgeEl ? badgeEl.outerHTML : '';

            // Feature chips (cloned from the card's hidden chips container)
            const chipsSource = card.querySelector('.room-vcard__chips');
            chipsEl.innerHTML = chipsSource ? chipsSource.innerHTML : '';

            // Gallery images: prefer per-room data-images, fall back to config gallery
            let images = [];
            try { images = JSON.parse(card.dataset.images || '[]'); } catch {}
            if (!images.length) {
                const config = card.closest('.room-section__config');
                images = Array.from(config?.querySelectorAll('.swiper-slide img') ?? [])
                    .map((img) => ({ url: img.src, alt: img.alt }));
            }

            // Rebuild swiper slides
            swiperWrap.innerHTML = images.map((img) =>
                `<div class="swiper-slide"><img src="${img.url}" alt="${img.alt}" class="room-modal__gallery-img" loading="lazy"></div>`
            ).join('');

            // Thumbnail strip (first 3 images)
            thumbsEl.innerHTML = images.slice(0, 3).map((img, i) =>
                `<div class="room-modal__thumb${i === 0 ? ' is-active' : ''}"><img src="${img.url}" alt="${img.alt}" loading="lazy"></div>`
            ).join('');

            // Gallery nav visibility
            navEl.style.display = images.length > 1 ? '' : 'none';

            // Reinitialise Swiper
            if (modalSwiper) { modalSwiper.destroy(true, true); modalSwiper = null; }
            const swiperEl = sheet.querySelector('.room-modal__swiper');
            modalSwiper = new Swiper(swiperEl, {
                slidesPerView: 1,
                on: {
                    slideChange(swiper) {
                        thumbsEl.querySelectorAll('.room-modal__thumb').forEach((t, i) =>
                            t.classList.toggle('is-active', i === swiper.activeIndex)
                        );
                    },
                },
            });

            prevBtn.onclick = () => modalSwiper?.slidePrev();
            nextBtn.onclick = () => modalSwiper?.slideNext();
            thumbsEl.querySelectorAll('.room-modal__thumb').forEach((thumb, i) => {
                thumb.onclick = () => modalSwiper?.slideTo(i);
            });

            // Active price tier
            const activeTier = card.querySelector('.room-vcard__price-tier.is-active');
            if (activeTier) {
                const amountEl = activeTier.querySelector('.room-vcard__amount');
                const termEl   = activeTier.querySelector('.room-vcard__term');
                priceRow.innerHTML = `
                    <span class="room-modal__amount">${amountEl ? amountEl.innerHTML : ''}</span>
                    <span class="room-modal__term">${termEl ? termEl.textContent : ''}</span>
                `;
            } else {
                priceRow.innerHTML = '';
            }

            // Apply / waitlist button
            const applyBtn = card.querySelector('.room-vcard__apply');
            applyWrap.innerHTML = applyBtn ? applyBtn.outerHTML : '';

            overlay.classList.add('is-open');
            document.body.style.overflow = 'hidden';
            // Swiper was initialised while the overlay was display:none so it
            // measured 0×0 dimensions. Update after the browser has painted the
            // visible modal so slides fill the aspect-ratio container correctly.
            requestAnimationFrame(() => { if (modalSwiper) modalSwiper.update(); });
        };

        const closeModal = () => {
            overlay.classList.remove('is-open');
            document.body.style.overflow = '';
        };

        // Close on backdrop click
        overlay.addEventListener('click', (e) => { if (e.target === overlay) closeModal(); });
        sheet.querySelector('.room-modal__close').addEventListener('click', closeModal);
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && overlay.classList.contains('is-open')) closeModal();
        });

        // Attach to all residence room vcards
        document.querySelectorAll('.room-vcard[data-room-card][data-images]').forEach((card) => {
            card.addEventListener('click', (e) => {
                if (window.innerWidth > 1024) return;
                if (e.target.closest('a, button')) return;
                openModal(card);
            });
        });
    },
};
